// maintenance.js
// 維護模式的簡易事件通知：apiService 收到後端回應 maintenance:true 時呼叫 notifyMaintenance，
// App 訂閱後切換成維護畫面。獨立成模組，避免 apiService（非 React）直接依賴元件。

/** 目前是否已進入維護狀態（模組層級，供非 React 程式碼查詢）。 */
let maintenanceState = { active: false, message: '' };

/** 已訂閱的 listener 集合。 */
const listeners = new Set();

/**
 * 通知「目前系統維護中」。重複呼叫相同內容不會重複觸發 listener。
 * @param {string} [message] 後端提供的維護訊息，缺少時由畫面使用預設文字
 */
export function notifyMaintenance(message) {
  const next = { active: true, message: typeof message === 'string' ? message : '' };
  if (maintenanceState.active && maintenanceState.message === next.message) {
    return;
  }
  maintenanceState = next;
  listeners.forEach((listener) => {
    try {
      listener(maintenanceState);
    } catch (e) {
      // listener 例外不可影響其他訂閱者或呼叫端的 API 流程
    }
  });
}

/**
 * 判斷後端回應內容是否為維護中。
 * @param {*} data 已解析的 JSON 回應
 * @returns {boolean}
 */
export function isMaintenanceResponse(data) {
  return !!data && typeof data === 'object' && data.maintenance === true;
}

/**
 * 訂閱維護狀態。若訂閱當下已處於維護中，會立即回呼一次。
 * @param {(state: {active: boolean, message: string}) => void} listener 回呼
 * @returns {() => void} 取消訂閱函式
 */
export function subscribeMaintenance(listener) {
  listeners.add(listener);
  if (maintenanceState.active) {
    listener(maintenanceState);
  }
  return () => listeners.delete(listener);
}

/** 放行金鑰在 localStorage 的儲存鍵。 */
const MAINT_KEY_STORAGE = 'agui_maint_key';

/** 放行金鑰在本機的有效時間（毫秒）：4 小時，與 BFF 放行 cookie 的預設效期一致；到期後需重新輸入。 */
export const MAINT_KEY_TTL_MS = 4 * 60 * 60 * 1000;

/** 附帶放行金鑰的 request header 名稱（BFF MaintenanceFilter 驗證）。 */
export const MAINT_KEY_HEADER = 'X-Maint-Key';

/**
 * 讀取已儲存的放行金鑰。localStorage 可能不可用（隱私模式等），失敗一律視為沒有。
 * @returns {string} 金鑰，沒有則為空字串
 */
export function getMaintKey() {
  try {
    const raw = window.localStorage.getItem(MAINT_KEY_STORAGE);
    if (!raw) {
      return '';
    }
    const saved = JSON.parse(raw);
    if (!saved || typeof saved.key !== 'string' || typeof saved.exp !== 'number' || saved.exp <= Date.now()) {
      // 已過期或格式不符：清掉，使用者需重新輸入
      window.localStorage.removeItem(MAINT_KEY_STORAGE);
      return '';
    }
    return saved.key;
  } catch (e) {
    return '';
  }
}

/**
 * 儲存放行金鑰。
 * @param {string} key 使用者在維護畫面隱藏入口輸入的金鑰
 */
export function setMaintKey(key) {
  try {
    window.localStorage.setItem(MAINT_KEY_STORAGE, JSON.stringify({ key, exp: Date.now() + MAINT_KEY_TTL_MS }));
  } catch (e) {
    // 儲存失敗就等於沒有放行，維護畫面會維持顯示
  }
}

/** 清除已儲存的放行金鑰（驗證失敗或金鑰已更換時）。 */
export function clearMaintKey() {
  try {
    window.localStorage.removeItem(MAINT_KEY_STORAGE);
  } catch (e) {
    // 忽略
  }
}

/**
 * 判斷請求網址是否指向 BFF（/bff/...）。放行金鑰只附帶在這類請求，不外洩給第三方網域。
 * @param {string} url 請求網址（絕對或相對）
 * @returns {boolean}
 */
export function isBffUrl(url) {
  return typeof url === 'string' && /(^|\/\/[^/]+)\/bff(\/|\?|$)/.test(url);
}

/**
 * 包裝全域 fetch：已儲存放行金鑰時，對 BFF 請求自動附上 X-Maint-Key header。
 * 這樣各處自行呼叫 fetch（apiService、附件下載等）都不必逐一修改。只會安裝一次。
 * 僅處理「字串或 URL 物件」形式的請求；Request 物件不改動。
 */
export function installMaintenanceKeyInjector() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function' || window.__maintKeyInjector) {
    return;
  }
  window.__maintKeyInjector = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const key = getMaintKey();
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : '';
    if (!key || !isBffUrl(url)) {
      return originalFetch(input, init);
    }
    const headers = new Headers((init && init.headers) || undefined);
    headers.set(MAINT_KEY_HEADER, key);
    return originalFetch(input, { ...(init || {}), headers });
  };
}
